import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search, Ban, CheckCircle, PauseCircle, Gem, Shield, Users as UsersIcon } from 'lucide-react';
import { api, compact, timeAgo } from '../../lib/api';
import { Avatar, SkeletonList, Sheet } from '../../components/ui';

export default function AdminUsers() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [sel, setSel] = useState(null);
  const [pts, setPts] = useState({ amount: '', reason: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['adminUsers', q, role, status],
    queryFn: () => api.get('/admin/users', { params: { q: q || undefined, role: role || undefined, status: status || undefined } }).then(r => r.data),
  });
  const { data: detail } = useQuery({
    queryKey: ['adminUser', sel?.id], queryFn: () => api.get(`/admin/users/${sel.id}`).then(r => r.data), enabled: !!sel,
  });

  const statusM = useMutation({
    mutationFn: ({ id, status, reason }) => api.post(`/admin/users/${id}/status`, { status, reason }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries(['adminUsers']); },
    onError: e => toast.error(e.message),
  });
  const roleM = useMutation({
    mutationFn: ({ id, role, producer_kind }) => api.post(`/admin/users/${id}/role`, { role, producer_kind }),
    onSuccess: () => { toast.success('Role updated'); qc.invalidateQueries(['adminUsers']); },
  });
  const pointsM = useMutation({
    mutationFn: ({ id, amount, reason }) => api.post(`/admin/users/${id}/points`, { amount, reason }),
    onSuccess: () => { toast.success('Points adjusted'); setPts({ amount: '', reason: '' }); qc.invalidateQueries(['adminUsers', 'adminUser']); },
    onError: e => toast.error(e.message),
  });
  const banIpM = useMutation({
    mutationFn: ({ ip, blockUsers }) => api.post('/admin/ip-bans', { ip, blockUsers, reason: 'Admin action' }),
    onSuccess: () => toast.success('IP banned'),
  });

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400" />
        <input className="input pl-10" placeholder="Search username, email or IP…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {[['', 'All roles'], ['user', 'Users'], ['producer', 'Producers'], ['admin', 'Admins']].map(([k, l]) => (
          <button key={k} onClick={() => setRole(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${role === k ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
        {[['', 'Any'], ['active', 'Active'], ['suspended', 'Suspended'], ['blocked', 'Blocked']].map(([k, l]) => (
          <button key={'s' + k} onClick={() => setStatus(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${status === k ? 'bg-brand-500 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
      </div>

      {isLoading ? <SkeletonList n={8} h="h-16" /> : (
        <div className="card p-2 space-y-1">
          {data.map(u => (
            <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
              <button onClick={() => setSel(u)}><Avatar src={u.avatar_url} name={u.username} size={38} /></button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => setSel(u)} className="font-bold text-sm hover:underline">{u.username}</button>
                  <span className={`chip ${u.role === 'admin' ? 'bg-ink-900 text-white' : u.role === 'producer' ? 'chip-mint' : 'chip-ink'}`}>{u.role}</span>
                  {u.status !== 'active' && <span className="chip bg-red-100 text-red-600">{u.status}</span>}
                  {u.city_handle && <span className="chip-brand">{u.city_handle}</span>}
                </div>
                <p className="text-[11px] text-ink-400 font-semibold truncate">
                  {u.email} · IP {u.last_ip || '—'} · {u.purchases} orders · {u.invited} invites
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-black text-xs text-brand-600">💎{compact(u.points_total)}</p>
                <p className="text-[10px] text-ink-400 font-bold">{u.level ? `L${u.level}` : '—'}</p>
              </div>
            </div>))}
        </div>
      )}

      <Sheet open={!!sel} onClose={() => setSel(null)} title={sel ? `@${sel.username}` : ''} wide>
        {detail && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar src={detail.user.avatar_url} name={detail.user.username} size={54} />
              <div className="flex-1 min-w-0">
                <p className="font-bold">{detail.user.display_name || detail.user.username}</p>
                <p className="text-xs text-ink-400 font-semibold">{detail.user.email}</p>
                <p className="text-[11px] text-ink-400 font-semibold">
                  IP {detail.user.last_ip} · joined {timeAgo(detail.user.created_at)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <B l="Balance" v={compact(detail.user.points_balance)} />
              <B l="Lifetime" v={compact(detail.user.points_total)} />
              <B l="Level" v={detail.hero?.level || '—'} />
            </div>

            <div>
              <p className="label">Account status</p>
              <div className="grid grid-cols-3 gap-2">
                {[['active', CheckCircle, 'Activate', 'btn-mint'], ['suspended', PauseCircle, 'Suspend', 'btn-ghost'], ['blocked', Ban, 'Block', 'btn bg-red-500 text-white']].map(([k, Icon, l, cls]) => (
                  <button key={k} onClick={() => statusM.mutate({ id: sel.id, status: k })}
                    className={`${cls} btn-sm !py-2 ${detail.user.status === k ? 'ring-2 ring-offset-1 ring-ink-900' : ''}`}>
                    <Icon size={13} /> {l}
                  </button>))}
              </div>
            </div>

            <div>
              <p className="label">Role</p>
              <div className="grid grid-cols-3 gap-2">
                {['user', 'producer', 'admin'].map(rk => (
                  <button key={rk} onClick={() => roleM.mutate({ id: sel.id, role: rk, producer_kind: rk === 'producer' ? 'local' : null })}
                    className={`btn-ghost btn-sm ${detail.user.role === rk ? 'ring-2 ring-brand-400' : ''}`}>{rk}</button>))}
              </div>
            </div>

            <div>
              <p className="label">Adjust points</p>
              <div className="flex gap-2">
                <input className="input" type="number" placeholder="+500 or -200"
                  value={pts.amount} onChange={e => setPts({ ...pts, amount: e.target.value })} />
                <input className="input" placeholder="Reason" value={pts.reason}
                  onChange={e => setPts({ ...pts, reason: e.target.value })} />
                <button onClick={() => pointsM.mutate({ id: sel.id, amount: Number(pts.amount), reason: pts.reason })}
                  disabled={!pts.amount} className="btn-primary btn-sm shrink-0"><Gem size={13} /></button>
              </div>
            </div>

            {detail.user.last_ip && (
              <button onClick={() => confirm(`Ban IP ${detail.user.last_ip} and block all accounts on it?`) &&
                banIpM.mutate({ ip: detail.user.last_ip, blockUsers: true })}
                className="btn w-full bg-red-50 text-red-600 btn-sm"><Ban size={13} /> Ban IP {detail.user.last_ip}</button>)}

            {detail.sameIp?.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                <p className="text-xs font-black text-amber-800 flex items-center gap-1"><UsersIcon size={12} /> {detail.sameIp.length} other accounts on this IP</p>
                <p className="text-[11px] text-amber-700 font-semibold mt-1">
                  {detail.sameIp.map(x => `@${x.username}`).join(', ')}
                </p>
              </div>)}

            <div>
              <p className="label">Recent purchases</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {detail.purchases.map(p => (
                  <div key={p.id} className="flex justify-between text-xs p-2 rounded-lg bg-ink-50">
                    <span className="font-bold truncate">{p.shop_name}</span>
                    <span className="font-black">{p.amount} {p.currency} → +{p.total_points}💎</span>
                  </div>))}
                {!detail.purchases.length && <p className="text-xs text-ink-400">No purchases.</p>}
              </div>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}
function B({ l, v }) {
  return <div className="bg-ink-50 rounded-xl p-2"><p className="font-black text-sm">{v}</p><p className="text-[9px] font-bold text-ink-400 uppercase">{l}</p></div>;
}